import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import { CopyableValue } from 'components/CopyableValue/CopyableValue'
import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useState } from 'react'
import { deriveDemoLogins, DemoLoginEntry } from '../demo-credentials'

interface DemoLoginCredentialsProps {
  /** Stored company name; the "ZZ - DEMO - " prefix is stripped during derivation. */
  companyName?: string | null
  /** True once the employee population has completed. */
  isPopulated: boolean
  isLoading?: boolean
}

function CredentialRow({
  label,
  app,
  entry,
  passwordVisible,
  onTogglePassword,
}: {
  label: string
  /** Which app this login signs into. */
  app: 'admin-web-client' | 'enrollme'
  entry: DemoLoginEntry
  passwordVisible: boolean
  onTogglePassword: () => void
}) {
  const maskedPassword = '••••••••••••••••'

  return (
    <div
      style={{
        borderTop: '1px solid #f0f0f0',
        paddingTop: 14,
      }}
    >
      <Stack space={10}>
        <Stack direction="row" space={8} align="center">
          <UserOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
          <Typography size={13} style={{ fontWeight: 600, color: '#262626' }}>
            {label}
          </Typography>
          <Typography
            size={11}
            style={{
              color: '#8c8c8c',
              background: '#f5f5f5',
              borderRadius: 4,
              padding: '1px 6px',
            }}
          >
            {app}
          </Typography>
        </Stack>

        <Stack space={6}>
          <Stack direction="row" space={6} align="center">
            <Typography size={11} color="#8c8c8c" style={{ minWidth: 64 }}>
              Display name
            </Typography>
            <Typography size={12} style={{ color: '#595959' }}>
              {entry.name}
            </Typography>
          </Stack>

          <Stack direction="row" space={6} align="center">
            <Typography size={11} color="#8c8c8c" style={{ minWidth: 64 }}>
              Email
            </Typography>
            <div style={{ minWidth: 0, flex: 1 }}>
              <CopyableValue value={entry.email} />
            </div>
          </Stack>

          <Stack direction="row" space={6} align="center">
            <Typography size={11} color="#8c8c8c" style={{ minWidth: 64 }}>
              Password
            </Typography>
            {/* Same flex block as the email row — overflow:hidden clips the text so
                the icons stay immediately after it rather than being pushed to the edge */}
            <div
              style={{
                minWidth: 0,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                overflow: 'hidden',
              }}
            >
              <Typography
                size={13}
                style={{
                  color: '#595959',
                  letterSpacing: passwordVisible ? 0 : 2,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  userSelect: passwordVisible ? 'text' : 'none',
                  flexShrink: 1,
                }}
                aria-label={`Password: ${passwordVisible ? entry.password : 'hidden'}`}
              >
                {passwordVisible ? entry.password : maskedPassword}
              </Typography>
              {/* Copy icon always copies the real password */}
              <CopyableValue value={entry.password} iconOnly />
              {/* Eye toggle — inside the same block so it sits next to the copy icon */}
              <button
                type="button"
                onClick={onTogglePassword}
                aria-label={
                  passwordVisible ? 'Hide password' : 'Reveal password'
                }
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  color: '#8c8c8c',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {passwordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
          </Stack>
        </Stack>
      </Stack>
    </div>
  )
}

export default function DemoLoginCredentials({
  companyName,
  isPopulated,
  isLoading = false,
}: DemoLoginCredentialsProps) {
  const [passwordVisible, setPasswordVisible] = useState(false)

  const logins = isPopulated ? deriveDemoLogins(companyName) : null

  return (
    <ContentSection title="Demo Login Credentials" loading={isLoading}>
      {!isPopulated || logins === null ? (
        <Stack direction="row" space={8} align="center">
          <LockOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />
          <Typography size={13} color="#8c8c8c">
            Available after employees are populated
          </Typography>
        </Stack>
      ) : (
        <Stack space={0}>
          <CredentialRow
            label="HR Admin"
            app="admin-web-client"
            entry={logins.hrAdmin}
            passwordVisible={passwordVisible}
            onTogglePassword={() => setPasswordVisible((v) => !v)}
          />
          <CredentialRow
            label="Employee"
            app="enrollme"
            entry={logins.employee}
            passwordVisible={passwordVisible}
            onTogglePassword={() => setPasswordVisible((v) => !v)}
          />

          <div
            style={{
              marginTop: 16,
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderLeft: '4px solid #fa8c16',
              borderRadius: 6,
              padding: '8px 12px',
            }}
          >
            <Typography size={12} style={{ color: '#ad4e00' }}>
              These credentials are auto-generated from the company name. The
              password works only while it has not been changed — once the user
              changes their password, this value is no longer valid.
            </Typography>
          </div>
        </Stack>
      )}
    </ContentSection>
  )
}
